import axios from "axios";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const MAX_FILE = 5 * 1024 ** 3;
const DIRECT_LIMIT = 50 * 1024 * 1024;
const PART_WORKERS = 4;

// ---------- Progress store (read by UploadPanel) ----------
const blank = () => ({ active: false, label: "", total: 0, done: 0, skipped: 0, failed: [], bytesTotal: 0, bytesDone: 0, current: {} });
let state = blank();
const subs = new Set();
const emit = () => subs.forEach((fn) => fn({ ...state }));
export const uploadStore = {
  get: () => state,
  subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); },
  dismiss: () => { if (!state.active) { state = blank(); emit(); } },
};
let retryCtx = null;
const guard = (e) => { if (state.active) { e.preventDefault(); e.returnValue = ""; } };
window.addEventListener("beforeunload", guard);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function withRetry(fn, tries = 4) {
  for (let i = 0; ; i++) {
    try { return await fn(); } catch (e) {
      const st = e?.response?.status;
      if (i >= tries - 1 || (st && st < 500 && st !== 408 && st !== 429)) throw e;
      await sleep(1500 * 2 ** i);
    }
  }
}

// ---------- Large files: direct, resumable, multi-part upload to storage ----------
const resumeKey = (f, folderId) => `sdr-up:${folderId || "root"}:${f.name}:${f.size}:${f.lastModified}`;

async function startOrResume(file, folderId, opts) {
  const key = resumeKey(file, folderId);
  const saved = localStorage.getItem(key);
  if (saved) {
    try { const { data } = await api.get(`/uploads/${saved}`); return { ...data, key }; } catch { localStorage.removeItem(key); }
  }
  const { data } = await api.post("/uploads/init", { name: file.name, size: file.size, folder_id: folderId || null, auto_classify: opts.autoClassify, is_import: opts.isImport });
  localStorage.setItem(key, data.upload_id);
  return { ...data, done: [], key };
}

async function uploadLarge(file, folderId, opts, onBytes) {
  const up = await startOrResume(file, folderId, opts);
  const partBytes = (n) => Math.min(up.part_size, file.size - (n - 1) * up.part_size);
  const done = new Set(up.done);
  let sent = [...done].reduce((s, n) => s + partBytes(n), 0);
  onBytes(sent);
  const todo = Array.from({ length: up.parts }, (_, i) => i + 1).filter((n) => !done.has(n));
  const live = {};
  const worker = async () => {
    while (todo.length) {
      const batch = todo.splice(0, 20);
      const { data } = await withRetry(() => api.post(`/uploads/${up.upload_id}/urls`, { parts: batch }));
      for (const n of batch) {
        const blob = file.slice((n - 1) * up.part_size, (n - 1) * up.part_size + partBytes(n));
        await withRetry(() => axios.put(data.urls[n], blob, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (e) => { live[n] = e.loaded; onBytes(sent + Object.values(live).reduce((a, b) => a + b, 0)); },
        }), 6);
        delete live[n]; sent += partBytes(n); onBytes(sent);
      }
    }
  };
  await Promise.all(Array.from({ length: PART_WORKERS }, worker));
  const { data } = await withRetry(() => api.post(`/uploads/${up.upload_id}/complete`));
  localStorage.removeItem(up.key);
  return data;
}

async function uploadSmall(file, folderId, opts, onBytes) {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("folder_id", folderId || "");
  form.append("auto_classify", opts.autoClassify ? "true" : "false");
  form.append("is_import", opts.isImport ? "true" : "false");
  const { data } = await withRetry(() => api.post("/files/upload", form, { onUploadProgress: (e) => onBytes(e.loaded) }));
  return data;
}

export const uploadOne = (file, folderId, opts, onBytes = () => {}) =>
  (file.size > DIRECT_LIMIT ? uploadLarge : uploadSmall)(file, folderId, opts, onBytes);

// ---------- Batch runner ----------
async function runBatch(label, jobs, opts) {
  state = { ...blank(), active: true, label, total: jobs.length + (opts.skipped || 0), skipped: opts.skipped || 0, done: opts.skipped || 0,
    bytesTotal: jobs.reduce((s, j) => s + j.file.size, 0) };
  emit();
  const base = {};
  let idx = 0, ok = 0;
  const failed = [];
  const worker = async () => {
    while (idx < jobs.length) {
      const job = jobs[idx++];
      const id = `${idx}`;
      state.current[id] = { name: job.file.name, pct: 0 };
      const onBytes = (b) => {
        base[id] = b; state.bytesDone = Object.values(base).reduce((a, c) => a + c, 0);
        state.current[id] = { name: job.file.name, pct: Math.round((b / job.file.size) * 100) }; emit();
      };
      try {
        const data = await uploadOne(job.file, job.folderId, opts, onBytes);
        ok += 1;
        if (data?.duplicates?.length) toast.warning(`Doublon : « ${job.file.name} » identique à « ${data.duplicates.map((d) => d.name).join(", ")} »`, { duration: 8000 });
      } catch (e) {
        failed.push({ ...job, error: errMsg(e) });
        base[id] = job.file.size;
      }
      delete state.current[id];
      state.done += 1; state.failed = [...failed]; emit();
    }
  };
  await Promise.all(Array.from({ length: opts.workers || 3 }, worker));
  state.active = false; state.bytesDone = state.bytesTotal; emit();
  retryCtx = failed.length ? { label, jobs: failed.map(({ file, folderId }) => ({ file, folderId })), opts: { ...opts, skipped: 0 } } : null;
  if (failed.length) toast.error(`${ok} fichier(s) envoyé(s), ${failed.length} en échec — voir le panneau d'envoi`);
  else toast.success(`${ok} fichier(s) envoyé(s)${opts.skipped ? `, ${opts.skipped} déjà présent(s) ignoré(s)` : ""}`);
  window.dispatchEvent(new Event("sdr-uploads-finished"));
  return ok;
}

export const retryFailed = () => (retryCtx && !state.active ? runBatch(retryCtx.label, retryCtx.jobs, retryCtx.opts) : 0);

const oversize = (files) => {
  const big = files.filter((f) => f.size > MAX_FILE);
  const empty = files.filter((f) => !f.size);
  if (big.length) toast.warning(`${big.length} fichier(s) de plus de 5 Go ignoré(s)`);
  if (empty.length) toast.warning(`${empty.length} fichier(s) vide(s) (0 octet) ignoré(s)`);
  return files.filter((f) => f.size <= MAX_FILE && f.size > 0);
};

export async function uploadFiles(fileList, folderId, { autoClassify = true, onDone } = {}) {
  if (state.active) { toast.error("Un envoi est déjà en cours, patientez jusqu'à la fin"); return 0; }
  const files = oversize(Array.from(fileList || []));
  if (!files.length) return 0;
  return runBatch(files.length === 1 ? files[0].name : `${files.length} fichiers`, files.map((file) => ({ file, folderId })),
    { autoClassify, isImport: false, workers: 2, onDone });
}

const SKIP = /^(\.DS_Store|Thumbs\.db|desktop\.ini|~\$.*|\._.*)$/i;

async function readEntry(entry, prefix, out) {
  if (entry.isFile) {
    const file = await new Promise((res, rej) => entry.file(res, rej));
    if (!SKIP.test(file.name)) out.push({ file, dir: prefix });
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const dir = [...prefix, entry.name];
    let batch;
    do {
      batch = await new Promise((res, rej) => reader.readEntries(res, rej));
      for (const e of batch) await readEntry(e, dir, out);
    } while (batch.length);
  }
}

// Returns [{file, dir: [folder names]}] from a drop event, or null when no folder was dropped
export async function entriesFromDrop(dataTransfer) {
  const entries = Array.from(dataTransfer.items || []).map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.some((e) => e.isDirectory)) return null;
  const out = [];
  for (const e of entries) await readEntry(e, [], out);
  return out;
}

export const entriesFromInput = (fileList) => Array.from(fileList || [])
  .filter((f) => !SKIP.test(f.name))
  .map((f) => ({ file: f, dir: (f.webkitRelativePath || f.name).split("/").slice(0, -1) }));

export async function uploadTree(items, parentId, { onDone } = {}) {
  if (!items.length) return 0;
  if (state.active) { toast.error("Un envoi est déjà en cours, patientez jusqu'à la fin"); return 0; }
  const keep = new Set(oversize(items.map((i) => i.file)));
  const list = items.filter((i) => keep.has(i.file));
  const tid = toast.loading("Préparation des dossiers…");
  let jobs, skipped = 0;
  try {
    const paths = [...new Map(list.map((i) => [i.dir.join("/"), i.dir])).values()].filter((p) => p.length);
    const { data: { folders: map } } = await api.post("/folders/ensure-paths", { parent_id: parentId || null, paths });
    jobs = list.map(({ file, dir }) => ({ file, folderId: (dir.length ? map[dir.join("/")] : parentId) || null }));
    const found = new Set();
    for (let i = 0; i < jobs.length; i += 2000) {
      const chunk = jobs.slice(i, i + 2000);
      const { data } = await api.post("/files/existing", { items: chunk.map((j) => ({ folder_id: j.folderId, name: j.file.name, size: j.file.size })) });
      data.existing.forEach((k) => found.add(i + k));
    }
    skipped = found.size;
    jobs = jobs.filter((_, i) => !found.has(i));
    toast.dismiss(tid);
  } catch (e) {
    toast.error(errMsg(e), { id: tid });
    return 0;
  }
  const root = list[0]?.dir[0] || "dossier";
  if (!jobs.length) { toast.success(`Tout est déjà en ligne (${skipped} fichier(s))`); return 0; }
  return runBatch(`Import « ${root} »`, jobs, { autoClassify: false, isImport: true, workers: 3, skipped, onDone });
}
